import { Injectable, Injector, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { SubscriptionService } from '../api/subscription.service';
import { LocaleService } from '../i18n';
import {
  Observable,
  BehaviorSubject,
  EMPTY,
  tap,
  catchError,
  throwError,
  map,
  shareReplay,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '@millim/shared-types';
import { LoginInput, RegisterInput, UpdateProfileInput } from '@millim/validation-schemas';

export interface AuthResponse {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken: string;
    user: User;
  };
  timestamp: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private injector = inject(Injector);
  private localeService = inject(LocaleService);

  // Signals for reactive state
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  public currentUser = signal<User | null>(null);
  /* v8 ignore next */
  public isAuthenticated = computed(() => this.currentUser() !== null);
  /* v8 ignore next */
  public isAdmin = computed(() => this.currentUser()?.role === 'admin');

  constructor() {
    // Load user from localStorage on init
    this.loadUserFromStorage();
  }

  /**
   * Register a new user
   */
  register(
    data: RegisterInput,
  ): Observable<ApiResponse<{ userId: string; email: string; name: string; message: string }>> {
    return this.http.post<
      ApiResponse<{ userId: string; email: string; name: string; message: string }>
    >(`${environment.apiUrl}/auth/register`, data);
  }

  /**
   * Verify email with token
   */
  verifyEmail(token: string): Observable<ApiResponse<{ message: string; userId: string }>> {
    return this.http.post<ApiResponse<{ message: string; userId: string }>>(
      `${environment.apiUrl}/auth/verify-email`,
      { token },
    );
  }

  /**
   * Login user
   */
  login(credentials: LoginInput): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/login`, credentials).pipe(
      tap((response) => {
        if (response.success) {
          this.setSession(response.data);
        }
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Logout user
   */
  logout(): Observable<unknown> {
    const refreshToken = this.getRefreshToken();
    this.clearSession();
    this.router.navigate(['/login']);

    const logoutObs = this.http
      .post<unknown>(`${environment.apiUrl}/auth/logout`, { refreshToken })
      .pipe(
        catchError(() => EMPTY),
        shareReplay(1),
      );
    logoutObs.subscribe();
    return logoutObs;
  }

  /**
   * Refresh access token
   */
  refreshToken(): Observable<{ accessToken: string; refreshToken: string }> {
    const refreshToken = this.getRefreshToken();

    return this.http
      .post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
        `${environment.apiUrl}/auth/refresh`,
        { refreshToken },
      )
      .pipe(
        tap((response) => {
          if (response.success) {
            this.setTokens(response.data.accessToken, response.data.refreshToken);
          }
        }),
        map((response) => response.data),
        catchError((error) => {
          // If refresh fails, logout user
          this.clearSession();
          this.router.navigate(['/login']);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Get current user profile
   */
  getProfile(): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${environment.apiUrl}/auth/profile`).pipe(
      tap((response) => {
        if (response.success) {
          this.setUser(response.data);
          this.handleProfile(response.data);
        }
      }),
    );
  }

  /**
   * Update user profile
   */
  updateProfile(data: UpdateProfileInput): Observable<ApiResponse<User>> {
    return this.http.patch<ApiResponse<User>>(`${environment.apiUrl}/auth/profile`, data).pipe(
      tap((response) => {
        if (response.success) {
          this.setUser(response.data);
          this.handleProfile(response.data);
        }
      }),
    );
  }

  /**
   * Request password reset
   */
  forgotPassword(email: string): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(
      `${environment.apiUrl}/auth/forgot-password`,
      { email },
    );
  }

  /**
   * Reset password with token
   */
  resetPassword(token: string, newPassword: string): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(
      `${environment.apiUrl}/auth/reset-password`,
      { token, newPassword },
    );
  }

  /**
   * Change password for authenticated user
   */
  changePassword(
    currentPassword: string,
    newPassword: string,
  ): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(
      `${environment.apiUrl}/auth/change-password`,
      { currentPassword, newPassword },
    );
  }

  /**
   * Resend verification email
   */
  resendVerification(email: string): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(
      `${environment.apiUrl}/auth/resend-verification`,
      { email },
    );
  }

  // ==================== Helper Methods ====================

  /**
   * Sync locale from user profile and auto-write to server if needed.
   * Returns 'synced' when profile has a locale, 'needs-write' when it doesn't.
   */
  private handleProfile(user: User): void {
    const result = this.localeService.syncFromProfile(user);
    if (result === 'needs-write') {
      this.syncLocaleToServer(this.localeService.locale());
    }
  }

  /**
   * PATCH the current locale to /auth/profile.
   * Used for legacy users with no server locale on first login, AND for the
   * locale switcher so explicit user choices survive page reloads (otherwise
   * syncFromProfile overrides localStorage with the stale server value).
   * Fire-and-forget — only called when there's an authenticated session.
   */
  syncLocaleToServer(locale: 'en' | 'fr'): void {
    if (!this.currentUser()) return;
    this.http.patch<ApiResponse<User>>(`${environment.apiUrl}/auth/profile`, { locale }).subscribe({
      error: (error) => {
        console.warn('Failed to sync locale to server:', error);
      },
    });
  }

  /**
   * Set user session after login
   */
  private setSession(data: { accessToken: string; refreshToken: string; user: User }): void {
    this.setTokens(data.accessToken, data.refreshToken);
    this.setUser(data.user);
    this.handleProfile(data.user);
  }

  /**
   * Set tokens in localStorage
   */
  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(environment.auth.tokenKey, accessToken);
    localStorage.setItem(environment.auth.refreshTokenKey, refreshToken);
  }

  /**
   * Set user in state and localStorage
   */
  private setUser(user: User): void {
    this.currentUser.set(user);
    this.currentUserSubject.next(user);
    localStorage.setItem(environment.auth.userKey, JSON.stringify(user));
  }

  /**
   * Clear user session
   */
  private clearSession(): void {
    localStorage.removeItem(environment.auth.tokenKey);
    localStorage.removeItem(environment.auth.refreshTokenKey);
    localStorage.removeItem(environment.auth.userKey);
    this.currentUser.set(null);
    this.currentUserSubject.next(null);
    // Lazy-resolved to avoid circular dep via the auth interceptor.
    this.injector.get(SubscriptionService).reset();
  }

  /**
   * Load user from localStorage on app init
   */
  private loadUserFromStorage(): void {
    const userJson = localStorage.getItem(environment.auth.userKey);
    if (userJson) {
      try {
        const user = JSON.parse(userJson) as User;
        this.currentUser.set(user);
        this.currentUserSubject.next(user);
      } catch (error) {
        console.error('Failed to parse user from localStorage:', error);
        this.clearSession();
      }
    }
  }

  /**
   * Get access token
   */
  public getAccessToken(): string | null {
    return localStorage.getItem(environment.auth.tokenKey);
  }

  /**
   * Get refresh token
   */
  public getRefreshToken(): string | null {
    return localStorage.getItem(environment.auth.refreshTokenKey);
  }

  /**
   * Check if user has access token
   */
  public hasToken(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: unknown): Observable<never> {
    console.error('Auth service error:', error);
    return throwError(() => error);
  }
}
